/** #190 — an origin-wide tombstone of refresh-token HASHES.
 *
 *  Chrome's "reopen closed tab" restores the tab's `sessionStorage` snapshot,
 *  so a restored tab comes back holding a refresh token the original tab —
 *  now gone — may already have rotated away. There is no contention for the
 *  #184 Web Lock and no peer left to broadcast to, so nothing in the live-tab
 *  machinery can see it: the restored tab's first refresh is a SEQUENTIAL
 *  reuse of a long-spent token, and Guardius judges that `theft` rather than
 *  merely invalid (the mechanism and the grace window are AGENTS.md's to
 *  document, not this file's).
 *
 *  The entire mechanism is one storage asymmetry: `sessionStorage` is rolled
 *  back by a tab restore and `localStorage` is NOT. So the question this
 *  module answers is never "how did this tab get here?" — a reload and a
 *  restore are indistinguishable, and dropping the token on boot would sign
 *  users out constantly — but "is THIS TOKEN already dead?", which is a fact
 *  about the token and survives the restore in `localStorage`. A reload whose
 *  token was never rotated away finds no tombstone and refreshes normally.
 *
 *  Keyed on the token's own hash, never on a monotonic generation counter.
 *  The counter is the obvious cheaper variant and is wrong: keyed on a global
 *  ordering rather than on the token's identity, it makes two independent
 *  sign-ins in one browser read each other's rotations as proof their own
 *  LIVE token is stale.
 *
 *  A HASH and never the token. `localStorage` is origin-wide and survives a
 *  tab close, so a raw refresh token there would be strictly worse than
 *  today's `sessionStorage`.
 *
 *  BEST-EFFORT BY CONSTRUCTION. Every path degrades toward today's behaviour:
 *  an unavailable or throwing `crypto.subtle` (insecure context) or
 *  `localStorage` (private mode, blocked site data) answers "not tombstoned"
 *  and silently drops the write. That failure mode is #190 itself — the bug
 *  we already have — whereas failing the other way would be a sign-out storm
 *  on every browser that blocks site data. Do not "harden" these catches into
 *  throws or sign-outs. */

/** Origin-wide, so namespaced like every other Console storage key. */
export const TOMBSTONE_KEY = 'watchtower.refresh.tombstones';

/** 7 days, because Guardius's `REFRESH_TOKEN_EXPIRE_DAYS = 7`
 *  (`server/src/shared/services/jwt/keys.ts:18`). A tombstone older than the
 *  longest a refresh token can possibly live can never be consulted again —
 *  no live token hashes to it — so pruning at the TTL loses nothing and
 *  bounds growth for an idle origin. */
export const TOMBSTONE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** 1024 entries, sized so the TTL — not the cap — is what normally binds.
 *
 *  The exposure this module closes is bounded by the refresh token's own 7-day
 *  life, so the list has to be able to span that life or it stops answering
 *  about tokens that are still dangerous. A session rotating every ~14 minutes
 *  writes ~100 tombstones a day, so one chain needs ~700 for the full 7 days;
 *  1024 leaves headroom for several concurrent sign-ins sharing the origin.
 *
 *  A much smaller cap looks tempting and is the wrong trade: at 64 the window
 *  is the last ~15 hours of one chain, so a duplicated tab left open overnight
 *  and reopened next morning is already past it — while Chrome keeps its
 *  recently-closed list for days, which is exactly the #190 window. The
 *  storage cost does not argue the other way: an entry serializes to ~90
 *  bytes, so the whole list is ~90 KB against a multi-MB origin quota.
 *
 *  Trimming drops the OLDEST first: the newest tombstones are the ones a
 *  restored tab is most likely to ask about. */
export const TOMBSTONE_MAX_ENTRIES = 1024;

/** One tombstone: `h` the SHA-256 hex digest of a refresh token, `t`
 *  `Date.now()` at the moment it was written. Deliberately short keys — this
 *  lives in a shared, size-limited store and 64 of them are serialized on
 *  every write. */
interface Tombstone {
  h: string;
  t: number;
}

function isTombstone(value: unknown): value is Tombstone {
  if (!value || typeof value !== 'object') return false;
  const { h, t } = value as { h?: unknown; t?: unknown };
  return typeof h === 'string' && typeof t === 'number' && Number.isFinite(t);
}

/** SHA-256 of `token`, lowercase hex, or `null` when it cannot be computed.
 *
 *  `crypto.subtle` is read at CALL time, never captured at module load: it is
 *  absent in an insecure context, and a caller must get the degraded `null`
 *  rather than a module that threw on import. A `null` hash means nothing
 *  identifies the token, which every caller below treats as "no tombstone
 *  machinery available" — not as "not tombstoned, definitively". */
export async function hashRefreshToken(token: string): Promise<string | null> {
  const subtle = (globalThis as { crypto?: Crypto }).crypto?.subtle;
  if (typeof subtle?.digest !== 'function') return null;
  try {
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(token));
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
}

/** The stored list, pruned of entries past the TTL — or `null` when storage
 *  could not be READ at all.
 *
 *  That distinction is load-bearing, and collapsing it to `[]` is a data-loss
 *  bug: `[]` must mean "there is nothing valid stored here", so the next write
 *  may overwrite (an absent key, or a value that is not an array of
 *  tombstones). "The read threw, so the contents are UNKNOWN" is a different
 *  answer, and treating it as empty lets one write clobber up to
 *  TOMBSTONE_MAX_ENTRIES - 1 valid tombstones — every one of them a token
 *  whose re-presentation is revoke-all.
 *
 *  Malformed ENTRIES inside a well-formed array are dropped individually
 *  rather than discarding the whole list: a lost tombstone is the #190 path,
 *  so keeping every entry we can still understand is the safer direction. */
function readTombstones(now: number): Tombstone[] | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(TOMBSTONE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.filter(isTombstone).filter((e) => now - e.t <= TOMBSTONE_TTL_MS);
}

/** Record that `token` must never be presented again.
 *
 *  Called on every rotation and on every discard, so it is on the refresh hot
 *  path: it resolves rather than rejecting on any failure. */
export async function tombstoneRefreshToken(token: string): Promise<void> {
  const h = await hashRefreshToken(token);
  // No hash means nothing would identify the entry, so there is nothing to
  // store. Return BEFORE touching storage: writing an empty array here would
  // be a pure side effect with no information in it.
  if (h === null) return;

  const now = Date.now();
  const entries = readTombstones(now);
  // Contents unknown (the read threw). Writing now would replace tombstones we
  // cannot see with a one-element list; declining to record this one is the
  // smaller loss and is the documented degrade direction.
  if (entries === null) return;

  const kept = entries.filter((e) => e.h !== h);
  kept.push({ h, t: now });
  // Trim by INSERTION ORDER — deliberately NOT by `t`, and a future reader's
  // instinct to "fix" this back to a sort is the bug. This module is the sole
  // writer and only ever appends (a de-dupe removes, then re-appends), so the
  // last element is always the newest and insertion order carries no clock.
  // Sorting by `t` and keeping the largest breaks under a BACKWARDS clock jump
  // — a dead-CMOS laptop booting behind the clock that wrote the store: the
  // new entry's `t` is then the smallest of the MAX+1 and is exactly what gets
  // trimmed, so the one tombstone that mattered is dropped. It is
  // self-sustaining, too, because `now - t` is negative for the future-dated
  // entries and the TTL filter can never clear them: the module would record
  // nothing at all, silently, until the clock was fixed.
  const trimmed = kept.slice(-TOMBSTONE_MAX_ENTRIES);

  try {
    localStorage.setItem(TOMBSTONE_KEY, JSON.stringify(trimmed));
  } catch {
    // Quota, private mode, blocked site data. Degrade to today's behaviour.
  }
}

/** Whether `token` has been discarded or rotated away by any tab on this
 *  origin. `false` whenever the answer cannot be established — an
 *  unavailable `crypto.subtle`, unreadable storage, a pruned or absent entry.
 *
 *  Pruning here is read-only: the expired entries are filtered out of the
 *  answer but not written back. The cap-on-write already bounds growth, and a
 *  predicate on the refresh hot path has no business mutating shared storage. */
export async function isRefreshTokenTombstoned(token: string): Promise<boolean> {
  const h = await hashRefreshToken(token);
  if (h === null) return false;
  // No try/catch here on purpose: `readTombstones` owns every throwing path
  // (it returns `null` for an unreadable store) and `Array.some` over a
  // validated list cannot throw. An outer catch would be dead code that also
  // swallows the inner guard's failures, so the test that names the read path
  // would pass with that guard deleted.
  const entries = readTombstones(Date.now());
  return entries !== null && entries.some((e) => e.h === h);
}
